import { InputNumber } from "@/components/InputNumber";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateBailAccountSettings,
  useCreateCommissionsLevelBailAccountSettings,
  useUpdateBailAccountSettings,
  useUpdateCommissionsLevelBailAccountSettings,
} from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { BailAccountSettings, CreateUpdateBailAccountSettings } from "../../types";

const colSpan = 12;

interface Props {
  settings?: BailAccountSettings;
  open: boolean;
  onFormCancel?: () => void;
  viewType: "commissions-level" | "agent-commissions";
  parentId: string;
}

export const BailAccountSettingsForm = ({ settings, open, onFormCancel, viewType, parentId }: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<CreateUpdateBailAccountSettings>();

  const createBailAccountSettings = useCreateBailAccountSettings();
  const updateBailAccountSettings = useUpdateBailAccountSettings();
  const createCommissionsLevelBailAccountSettings = useCreateCommissionsLevelBailAccountSettings();
  const updateCommissionsLevelBailAccountSettings = useUpdateCommissionsLevelBailAccountSettings();

  useEffect(() => {
    if (open && settings) {
      form.setFieldsValue(settings);
    }
  }, [open, settings, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (settings) {
          if (viewType === "agent-commissions") {
            updateBailAccountSettings.mutate(
              { id: parentId, object: values },
              {
                onSuccess: onFormCancel,
                onError: (error) => setErrorsToForm(form, "commissions.bailAccount.attrs", error.violations),
              },
            );
          } else {
            updateCommissionsLevelBailAccountSettings.mutate(
              { id: parentId, object: values },
              {
                onSuccess: onFormCancel,
                onError: (error) => setErrorsToForm(form, "commissions.bailAccount.attrs", error.violations),
              },
            );
          }
        } else {
          if (viewType === "agent-commissions") {
            createBailAccountSettings.mutate(
              { id: parentId, object: values },
              {
                onSuccess: onFormCancel,
                onError: (error) => setErrorsToForm(form, "commissions.bailAccount.attrs", error.violations),
              },
            );
          } else {
            createCommissionsLevelBailAccountSettings.mutate(
              { id: parentId, object: values },
              {
                onSuccess: onFormCancel,
                onError: (error) => setErrorsToForm(form, "commissions.bailAccount.attrs", error.violations),
              },
            );
          }
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={
        settings
          ? t("commissions.bailAccount.titles.updateSettings")
          : t("commissions.bailAccount.titles.createSettings")
      }
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={
        createCommissionsLevelBailAccountSettings.isPending ||
        updateCommissionsLevelBailAccountSettings.isPending ||
        createBailAccountSettings.isPending ||
        updateBailAccountSettings.isPending
      }
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="bailAccountSettingsForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="targetAmount"
              label={t("commissions.bailAccount.attrs.targetAmount")}
              rules={[validations.notNull, validations.minNumber(0.01)]}
            >
              <InputNumber type="decimal" addon="euro" />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={"rate"}
              label={t("commissions.bailAccount.attrs.rate")}
              rules={[validations.notNull, validations.minNumber(0.01), validations.maxNumber(100)]}
            >
              <InputNumber type="decimal" addon="percentage" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
